
import { CampingSpot, MapBounds } from '../types';
import { Capacitor } from '@capacitor/core';
import { obfuscate, deobfuscate } from '../utils/obfuscate';

const DB_NAME = 'fricamp_spot_cache';
const STORE_NAME = 'spots_by_grid';
const DB_VERSION = 1;
const GRID_SIZE = 0.05; // ~5 km cells
const CACHE_TTL = 60 * 60 * 1000; // 1 hour (brukt av eldre getCachedSpotsFromIDB + skrive-merge)

// --- Freshness-vinduer for stale-while-revalidate (getCachedSpotsWithMeta) ---
// «fresh»  → server fra cache og HOPP OVER nett-revalidering (nettopp hentet).
// «stale»  → server fra cache umiddelbart, men revalider mot nett i bakgrunnen.
// «miss»   → for gammelt (eller ingenting) → blokkerende nett-henting.
/** Ferskt-vindu i vanlige områder. */
export const FRESH_MS = 2 * 60 * 1000;        // 2 min
/** Ferskt-vindu i «hot» områder (du ser her ofte → data endres sjelden så raskt). */
export const HOT_FRESH_MS = 30 * 60 * 1000;   // 30 min
/** Ytre grense for å servere stale i det hele tatt. */
export const STALE_MAX_MS = 24 * 60 * 60 * 1000; // 24 t

export type CacheFreshness = 'fresh' | 'stale' | 'miss';

/**
 * Ren klassifisering (testbar): hvor ferskt er det eldste cellen vi serverer?
 * `isHot` forlenger fersk-vinduet for områder brukeren bruker mye.
 */
export const classifyFreshness = (
  oldestSavedAt: number | null,
  isHot: boolean,
  now: number = Date.now(),
): CacheFreshness => {
  if (oldestSavedAt == null) return 'miss';
  const age = now - oldestSavedAt;
  if (age > STALE_MAX_MS) return 'miss';
  return age <= (isHot ? HOT_FRESH_MS : FRESH_MS) ? 'fresh' : 'stale';
};

export interface CachedSpotsResult {
  spots: CampingSpot[];
  /** Eldste savedAt blant cellene vi returnerte (for freshness-klassifisering). null = ingen data. */
  oldestSavedAt: number | null;
}

const LS_KEY = 'fricamp_spot_ls_cache';
// iOS WKWebView localStorage = 5 MB. At ~400 bytes/spot (slim), 2000 spots ≈ 800 KB.
// Leaves plenty of room for other localStorage keys (~50 KB).
const LS_MAX_SPOTS = 2000;

// Skip IndexedDB entirely on iOS native (WKWebView bugs)
const isIOSNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

const gridKey = (lat: number, lon: number): string =>
  `${Math.floor(lat / GRID_SIZE)}_${Math.floor(lon / GRID_SIZE)}`;

const gridKeysForBounds = (bounds: MapBounds): string[] => {
  const keys: string[] = [];
  const minRow = Math.floor(bounds.latMin / GRID_SIZE);
  const maxRow = Math.floor(bounds.latMax / GRID_SIZE);
  const minCol = Math.floor(bounds.lonMin / GRID_SIZE);
  const maxCol = Math.floor(bounds.lonMax / GRID_SIZE);
  for (let r = minRow; r <= maxRow; r++) {
    for (let c = minCol; c <= maxCol; c++) {
      keys.push(`${r}_${c}`);
    }
  }
  return keys;
};

let dbPromise: Promise<IDBDatabase> | null = null;

const openDB = (): Promise<IDBDatabase> => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      dbPromise = null;
      reject(req.error);
    };
  });
  return dbPromise;
};

// Plass-lista lagres obfuskert (b), savedAt i klartekst for TTL-sjekk uten
// deobfuskering. Lett fartsdump mot rå eksport — se utils/obfuscate.ts.
interface GridEntry {
  b: string;
  savedAt: number;
}

const packSpots = (spots: CampingSpot[]): string => obfuscate(JSON.stringify(spots));

const unpackSpots = (entry: GridEntry | undefined): CampingSpot[] | null => {
  if (!entry) return null;
  const raw = deobfuscate(entry.b);
  if (raw == null) return null;
  try {
    return JSON.parse(raw) as CampingSpot[];
  } catch {
    return null;
  }
};

// --- localStorage fallback for iOS native ---
// Stores a flat array of stripped-down spots (marker-essential fields only)
// to keep within localStorage's ~5 MB limit.

interface LSCacheEntry {
  spots: Partial<CampingSpot>[];
  savedAt: number;
}

// Sentralisert obfuskert LS-lesing/skriving. Gammel klartekst-cache
// deobfuskerer til null → behandles som miss og repopuleres obfuskert.
const readLS = (): LSCacheEntry | null => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const json = deobfuscate(raw);
    if (json == null) return null;
    return JSON.parse(json) as LSCacheEntry;
  } catch {
    return null;
  }
};

const writeLS = (entry: LSCacheEntry): void => {
  localStorage.setItem(LS_KEY, obfuscate(JSON.stringify(entry)));
};

// Only keep fields needed for map markers + filtering.
// Full spot data (legal, aiResult, etc.) is fetched from Firestore when user taps a spot.
const stripSpotForLS = (s: CampingSpot): Partial<CampingSpot> => ({
  id: s.id,
  name: s.name,
  location: s.location,
  score: s.score,
  spotType: s.spotType,
  sourceLevel: s.sourceLevel,
  suitabilityColor: s.suitabilityColor,
  features: s.features,
  qualityTier: s.qualityTier,
  isVerified: s.isVerified,
  isInnmark: s.isInnmark,
  source: s.source,
  externalId: s.externalId,
});

const getCachedSpotsFromLS = (bounds: MapBounds): CampingSpot[] | null => {
  try {
    const entry = readLS();
    if (!entry) return null;
    if (Date.now() - entry.savedAt > CACHE_TTL) return null;
    // Filter to viewport
    const inBounds = entry.spots.filter(
      (s) =>
        s.location &&
        s.location.latitude >= bounds.latMin &&
        s.location.latitude <= bounds.latMax &&
        s.location.longitude >= bounds.lonMin &&
        s.location.longitude <= bounds.lonMax
    );
    return inBounds.length > 0 ? (inBounds as CampingSpot[]) : null;
  } catch {
    return null;
  }
};

const saveSpotsToLS = (spots: CampingSpot[]): void => {
  try {
    // Merge with existing
    const existing = readLS();
    const byId = new Map<string, Partial<CampingSpot>>();
    if (existing && Date.now() - existing.savedAt < CACHE_TTL) {
      for (const s of existing.spots) byId.set(s.id!, s);
    }
    for (const s of spots) byId.set(s.id, stripSpotForLS(s));
    // Keep only the top spots by quality tier + score to stay within limit
    const all = Array.from(byId.values());
    all.sort((a, b) => (a.qualityTier ?? 3) - (b.qualityTier ?? 3) || (b.score ?? 0) - (a.score ?? 0));
    const trimmed = all.slice(0, LS_MAX_SPOTS);
    writeLS({ spots: trimmed, savedAt: Date.now() });
  } catch {
    // localStorage full or unavailable — ignore
  }
};

export const getCachedSpotsFromIDB = async (bounds: MapBounds): Promise<CampingSpot[] | null> => {
  // On iOS native, use localStorage fallback instead of IndexedDB
  if (isIOSNative) return getCachedSpotsFromLS(bounds);
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const keys = gridKeysForBounds(bounds);
    const now = Date.now();
    const allSpots: CampingSpot[] = [];
    let hasAnyData = false;

    await Promise.all(
      keys.map(
        (key) =>
          new Promise<void>((resolve) => {
            const req = store.get(key);
            req.onsuccess = () => {
              const entry = req.result as GridEntry | undefined;
              if (entry && now - entry.savedAt < CACHE_TTL) {
                const spots = unpackSpots(entry);
                if (spots) {
                  hasAnyData = true;
                  allSpots.push(...spots);
                }
              }
              resolve();
            };
            req.onerror = () => resolve();
          })
      )
    );

    return hasAnyData ? allSpots : null;
  } catch {
    return null;
  }
};

/**
 * Som getCachedSpotsFromIDB, men (a) serverer stale data helt opp til STALE_MAX_MS
 * (ikke bare 1 t), og (b) rapporterer eldste savedAt så kalleren kan avgjøre om
 * nett-revalidering kan hoppes over (fersk) eller kjøres i bakgrunnen (stale).
 * Grunnlaget for stale-while-revalidate uten unødvendig «masse lasting».
 */
export const getCachedSpotsWithMeta = async (bounds: MapBounds): Promise<CachedSpotsResult> => {
  const now = Date.now();
  // iOS native: localStorage-blob med én savedAt.
  if (isIOSNative) {
    const entry = readLS();
    if (!entry || now - entry.savedAt > STALE_MAX_MS) return { spots: [], oldestSavedAt: null };
    const inBounds = entry.spots.filter(
      (s) =>
        s.location &&
        s.location.latitude >= bounds.latMin &&
        s.location.latitude <= bounds.latMax &&
        s.location.longitude >= bounds.lonMin &&
        s.location.longitude <= bounds.lonMax
    );
    return inBounds.length > 0
      ? { spots: inBounds as CampingSpot[], oldestSavedAt: entry.savedAt }
      : { spots: [], oldestSavedAt: null };
  }
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const keys = gridKeysForBounds(bounds);
    const allSpots: CampingSpot[] = [];
    let oldest: number | null = null;

    await Promise.all(
      keys.map(
        (key) =>
          new Promise<void>((resolve) => {
            const req = store.get(key);
            req.onsuccess = () => {
              const entry = req.result as GridEntry | undefined;
              if (entry && now - entry.savedAt <= STALE_MAX_MS) {
                const spots = unpackSpots(entry);
                if (spots && spots.length > 0) {
                  allSpots.push(...spots);
                  if (oldest == null || entry.savedAt < oldest) oldest = entry.savedAt;
                }
              }
              resolve();
            };
            req.onerror = () => resolve();
          })
      )
    );

    return { spots: allSpots, oldestSavedAt: oldest };
  } catch {
    return { spots: [], oldestSavedAt: null };
  }
};

export const saveSpotsToIDB = async (spots: CampingSpot[]): Promise<void> => {
  if (spots.length === 0) return;
  // On iOS native, use localStorage fallback
  if (isIOSNative) { saveSpotsToLS(spots); return; }
  try {
    const db = await openDB();
    // Group spots by grid cell
    const cells = new Map<string, CampingSpot[]>();
    for (const spot of spots) {
      const key = gridKey(spot.location.latitude, spot.location.longitude);
      const list = cells.get(key);
      if (list) list.push(spot);
      else cells.set(key, [spot]);
    }

    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const now = Date.now();

    // Merge with existing data in each cell
    const promises: Promise<void>[] = [];
    cells.forEach((newSpots, key) => {
      promises.push(
        new Promise<void>((resolve) => {
          const getReq = store.get(key);
          getReq.onsuccess = () => {
            const existing = getReq.result as GridEntry | undefined;
            let merged = newSpots;
            const existingSpots =
              existing && now - existing.savedAt < CACHE_TTL ? unpackSpots(existing) : null;
            if (existingSpots) {
              // Merge: new spots overwrite existing by ID
              const byId = new Map<string, CampingSpot>();
              for (const s of existingSpots) byId.set(s.id, s);
              for (const s of newSpots) byId.set(s.id, s);
              merged = Array.from(byId.values());
            }
            store.put({ b: packSpots(merged), savedAt: now } as GridEntry, key);
            resolve();
          };
          getReq.onerror = () => resolve();
        })
      );
    });

    await Promise.all(promises);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // Silently fail — cache is best-effort
  }
};

export const removeSpotFromIDB = async (spotId: string): Promise<void> => {
  // Remove from localStorage cache
  try {
    const entry = readLS();
    if (entry) {
      entry.spots = entry.spots.filter(s => s.id !== spotId);
      writeLS(entry);
    }
  } catch {}
  if (isIOSNative) return;
  // Remove from IDB — iterate all grid cells and remove the spot
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const allKeysReq = store.getAllKeys();
    await new Promise<void>((resolve) => {
      allKeysReq.onsuccess = async () => {
        for (const key of allKeysReq.result) {
          const getReq = store.get(key);
          await new Promise<void>((res) => {
            getReq.onsuccess = () => {
              const entry = getReq.result as GridEntry | undefined;
              const spots = unpackSpots(entry);
              if (entry && spots) {
                const filtered = spots.filter(s => s.id !== spotId);
                if (filtered.length !== spots.length) {
                  store.put({ b: packSpots(filtered), savedAt: entry.savedAt } as GridEntry, key);
                }
              }
              res();
            };
            getReq.onerror = () => res();
          });
        }
        resolve();
      };
      allKeysReq.onerror = () => resolve();
    });
  } catch {}
};

export const clearSpotIDB = async (): Promise<void> => {
  // Always clear localStorage cache (works on all platforms)
  try { localStorage.removeItem(LS_KEY); } catch {}
  if (isIOSNative) return;
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).clear();
    await new Promise<void>((resolve) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    // ignore
  }
};
